import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, OnDestroy} from "@angular/core";
import _ from "lodash";
import {WorkflowConfigService} from "@ispm/workflow/services/workflow-config.service";
import {QuestionnairesService} from "@ispm/widgets/components/questionnaires/services/questionnaire.service";
import {Store} from "@ngrx/store";
import {IgenomicsState} from "@ispm/core/interfaces/genomics-state.interface";
import {StringUtils} from "@ispm/shared/utils/String.utils";
import {TestService} from "@ispm/test/services/test.service";
import {RoutingChangeHandlerService} from "@ispm/layout/services/routing-change-handler.service";
import {IResourcesMap} from "@ispm/workflow/interfaces/resource-map.interface";
import {AuthenticationService} from "@ispm/user/services/authentication.service";
import {AberrationsService} from "@ispm/aberrations/services/aberrations.service";
import {FilterService} from "../../../../filter/services/filter.service";
import {CURRENT_ORDER_ACTIONS} from "@ispm/test/constants/current-order.constants";
import {FormHandlerService} from "@ispm/dynamic-form/services/form-handler.service";
import {TherapiesActionCreatorService} from "@ispm/therapies/services/therapies-action-creator.service";
import {switchMap, take, tap} from "rxjs/operators";
import {ActivatedRoute, Router} from "@angular/router";
import {AberrationService} from "@ispm/migration/services/aberration.service";
import {activeTabObject, buttonsNamesEnum} from "@ispm/test/constants/Tabs.constants";
import {TestActionsService} from "@ispm/test/services/test-actions.service";


@Component ({
  selector: 'gen-test-custom-tab',
  templateUrl: './test-custom-tab.component.html',
  styleUrls: ['./test-custom-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class TestCustomTabComponent implements OnInit, OnDestroy {

  private activeTabIndex: any;
  private prioritizeAvailable;
  private qc;
  private resourcesMap: IResourcesMap;
  private testTypeId;

  public selectedTab: string;
  public isFixedHeightTabPane: boolean;
  public verticalLeftContainerPatientView: boolean;
  public configuration;
  public workflowBarItems;
  public uiRelevantStatuses = ['review', '2nd opinion requested', '2nd opinion review', 'initial results', 'interpretation results'];
  public uiRelevantJobs = ['reviewTestResults', 'reviewResults', '2ndOpinionRequested', '2ndOpinionReview', 'initialResults', 'postTrials', 'TherapyMatch', 'InterpretationResults', 'therapymatchreflex', 'clinicalrialmatchreflex', 'clinicaltrialmatchreflex'];
  @Input() patientAnalyses: any;
  @Input() currentRevision;

  constructor(
    private store: Store<IgenomicsState>,
    private route: Router,
    private activatedRoute: ActivatedRoute,
    private authService: AuthenticationService,
    private workflowConfigService: WorkflowConfigService,
    private aberrationsService: AberrationsService,
    private filterService: FilterService,
    private questionnairesService: QuestionnairesService,
    private testService : TestService,
    private routeChangeHandler: RoutingChangeHandlerService,
    private aberrationService: AberrationService,
    private formHandlerService: FormHandlerService,
    private therapiesAC: TherapiesActionCreatorService,
    public detector: ChangeDetectorRef,
    public testActionsService: TestActionsService
  ) { }

  ngOnInit() {
      if (this.patientAnalyses.current.non_conformance_alert) {
        this.testActionsService.getAlerts(this.patientAnalyses.current.id).subscribe((alerts) => {
          if (alerts.length === 0) {
            this.patientAnalyses.current.non_conformance_alert = 0;
          } else {
            this.patientAnalyses.current.non_conformance_alert = alerts.filter(alert => alert.alert).length > 0 ? 1 : 2;
          }
        });
      }
      this.qc = this.patientAnalyses.current.qc;
      this.configuration = this.testService.testConfiguration;
      this.aberrationService.setCurrentRevision(this.currentRevision);
      this.testTypeId = this.testService.selectTestConfiguration(this.patientAnalyses.current, this.authService.user.organization);
      this.aberrationService.setTestTypeId(this.testTypeId);
      this.isFixedHeightTabPane = this.testService.testConfiguration && !this.testService.testConfiguration.isPathwayTest;
      this.verticalLeftContainerPatientView = this.testService.testConfiguration && !!this.testService.testConfiguration.VerticalLeftContainerPatientView;
      this.setPriorityOfTab();
      this.testService.updateButtonState(buttonsNamesEnum.UNASSIGN_CASE);
      this.workflowConfigService.getActiveWorkflow()
        .filter(workflowRequest => !!workflowRequest && !!this.patientAnalyses && !!this.patientAnalyses.current)
        .take(1)
        .subscribe(workflowRequest => {
          this.patientAnalyses.current.workflowRequest = workflowRequest;
          this.initRevisionData();
        });
  }

  private initRevisionData() {
    const url = this.route.url.split(/[?#]/)[0].split('/').pop();
    const currentTab = (url === 'specimens-and-samples')
      ? this.getAvailableTab(activeTabObject.SPECIMENS_AND_SAMPLES)
      : this.getAvailableTab(this.testService.activeTab);
    if (currentTab) {
      this.activeTabIndex = currentTab.index;
      this.selectedTab = currentTab.alias;
      this.testService.activeTab = currentTab;
      this.updateOpenPanel(currentTab.alias, currentTab.name);
    }

    if (this.showTab('aberrations')) {
      this.workflowConfigService.getResourcesMap()
        .toPromise()
        .then(resourcesMap => {
          this.resourcesMap = resourcesMap;
          this.aberrationService.setResourcesMap(resourcesMap);
            this.loadAberrations();
        });
    }
    if (this.showTab('prioritize')) {
      this.prefetchTestResources();
    }

    setTimeout(() => {
      //Clear current order details from the store after component was loaded (its data should be used only while loading)
      this.store.dispatch({type: CURRENT_ORDER_ACTIONS['INIT_CURRENT_ORDER_STATE']});
    }, 3000);
  }

  private getAvailableTab(tab) {
    //If the required tab is not visible (e.g. when moving from one revision of a test to a different revision in which the active tab is invisible),
    //return test info tab.
    if (!tab) {
      return tab;
    }
    let currentTab = tab;
    if (!this.showTab(tab.alias)) {
      currentTab = activeTabObject.TEST_INFO;
    }
    return currentTab;
  }

  private testConfigurationInitialized() {
    if (this.configuration && this.currentRevision) return true;
  }

  private forceHideTab(tabName) {
    return this.configuration && this.configuration.forceHideTabs
      && this.configuration.forceHideTabs.filter(tab => tab === tabName).length > 0;
  }

  private setPriorityOfTab () {
    let currentStep;
    for (let item of this.currentRevision.items) {
      if (item.name === this.currentRevision.activeJob.name) {
        currentStep = item;
      }
      if (item.name.toLowerCase() === 'interpretationresults') {
        let priorityInput;
        if (priorityInput = item.inputs.find(input => input.key === 'priorityAvailable')) {
          switch (item.inputs[0].value) {
            case 'optional':
              this.prioritizeAvailable = 1;
              break;
            case 'mandatory':
              this.prioritizeAvailable = 2;
              break;
            case 'false':
            default:
              this.prioritizeAvailable = 0;
              break;
          }
        } else {
          this.prioritizeAvailable = -1;
        }
      }
    }
  }

  private showTestSummaryAndReportPreviewTabs() {
    let documentRefExists = false;
    let xHtmlReportTemplates = [];
    xHtmlReportTemplates = this.configuration && this.configuration.xHtmlReportTemplates;
    if (xHtmlReportTemplates) {
      xHtmlReportTemplates.forEach((template) => {
        if (template.reference != null && template.labels != null) {
          documentRefExists = true;
        }
      });
    }
    return documentRefExists;
  }

  private isUIRelevantJob() {
    let uiStep: boolean = false;
    this.uiRelevantJobs.find((jobName) => {
      if(jobName.toLowerCase() === this.currentRevision.activeJob.type.toLowerCase()) {
        uiStep = true;
      }
    });
    return uiStep;
  }

  private showAberrationsTabWhileAnnotating() {
    let showAberration: boolean = false;
    if (this.workflowBarItems && this.workflowBarItems.items &&
      this.workflowBarItems.items.find(item => item.isActive).name.toLowerCase() === 'review' &&
      this.currentRevision.activeJob.type.toLowerCase() === 'annotationmatch') {
      showAberration = true;
    } else {
      showAberration = false;
    }
    return showAberration;
  }

  private  updateTheOpenPanel(panel, enumPanelRepresentation , replaceUrl = false) {
    this.testService.activeTab = activeTabObject[enumPanelRepresentation];
    this.route.navigate([panel], { relativeTo: this.activatedRoute, queryParamsHandling: 'preserve', replaceUrl: replaceUrl });
  }

  public cssClassFormat(className: string) {
    return StringUtils.cssClassFormat(className);
  }

  public updateOpenPanel(panel = 'info', enumPanelRepresentation = 'TEST_INFO', replaceUrl = false) {
    if (activeTabObject.hasOwnProperty(enumPanelRepresentation)) {
      this.selectedTab = panel;
      if (this.routeChangeHandler.unregisterAll()) {
        setTimeout(() => this.updateTheOpenPanel(panel, enumPanelRepresentation, replaceUrl));
      } else {
        this.updateTheOpenPanel(panel, enumPanelRepresentation, replaceUrl);
      }
    }
  }

  private loadAberrations() {
    let objectName = 'aberration';
    if (this.testService.activeTab.name === activeTabObject.GENOMIC_ABERRATIONS.name) return;
    this.testService.prefetchInProgress = true;
    this.aberrationsService.setIdsChunkAmount(this.configuration.idsChunkAmount);
    this.aberrationsService.setMaxAmount(this.configuration.maxQueriesAmount);
    this.aberrationsService.setChunkSize(this.configuration.chunkSize);
    let aberrationClassByNamesObj = this.aberrationService.getAberrationClassNames(this.configuration.requiredAnnotations);
    let loadFiltersRequests = [];
    if (aberrationClassByNamesObj.aberrationClassNames && aberrationClassByNamesObj.aberrationClassNames.length > 0) {
      this.filterService.clearAberrationObservable();
      aberrationClassByNamesObj.aberrationClassNames.forEach(aberrationClassName => {
        loadFiltersRequests.push(
          this.filterService.loadAppliedFilter(
            objectName,
            aberrationClassName,
            aberrationClassByNamesObj.defaultAberrationClassName,
            {
              testId: this.configuration.id,
              statisticsId: this.resourcesMap.Aberration || this.resourcesMap.firstRevision
            }
          )
            .toPromise()
            .then(currFilterAndQuery => {
              return currFilterAndQuery;
            })
        );
      });

      return Promise.all(loadFiltersRequests)
        .then(currFiltersAndQueries => {
          this.configuration.requiredAnnotations.forEach(typeMeta => {
            let filterQuery : any = {};
            if (['aberrationtype'].indexOf(typeMeta.name.toLowerCase()) === -1) return;
            if (typeMeta.isDefault)
              this.aberrationsService.setResultMessage(typeMeta.aberrationClass, false);
            this.testService.prefetchInProgress = false;
            this.detector.markForCheck();

            // TODO maybe .foreach instead of .find
            currFiltersAndQueries.find(currFilterAndQuery => {
              if (currFilterAndQuery) {
                let foundMatchingFilter = currFilterAndQuery.defaultFilter.context.find(context => context.reference.toLowerCase() == typeMeta.type.toLowerCase());
                if (foundMatchingFilter) {
                  let queryParams = Object.keys(currFilterAndQuery.queryObj);
                  if (queryParams) {
                    // we replace the [[ ]] with only one []
                    for (let i = 0; i < queryParams.length; i++) {
                      currFilterAndQuery.queryObj[queryParams[i]] = currFilterAndQuery.queryObj[queryParams[i]].split("[[").join("[").split("]]").join("]");
                    }
                  }
                  filterQuery.selectedFilterValues = currFilterAndQuery.queryObj;
                  filterQuery.filterType = typeMeta.type;
                }
              }
            });
            const modalityColumns = this.configuration.displayColumns[typeMeta.type];
            const defaultSort = modalityColumns.filter(c => c.defaultSort == 'true')[0];
            const sort = defaultSort ? defaultSort : modalityColumns[0];
            const sortBy = sort.sortable ? {column: sort.columnName, dir: 'true', sortAsExpression: sort.sortAsExpression} : {column: undefined, dir: undefined, sortAsExpression: undefined};
            this.aberrationsService.getAberrationsListByType(
              this.patientAnalyses.current.workflowRequest.id,
              this.resourcesMap.Aberration || this.resourcesMap.firstRevision,
              typeMeta.type,
              typeMeta.aberrationClass,
              filterQuery,
              this.configuration.id,
              true,
              0,
              this.aberrationsService.getIdsChunkAmount(),
              this.resourcesMap.requestedRevision,
              sortBy
            ).subscribe(
              (err) => {
                return {page: null, ids: [], last: true};
              })
            this.detector.markForCheck();
            this.detector.detectChanges();
          })
        })
    }
  }

  private prefetchTestResources() {
    this.therapiesAC.prefetchTestResources()
      .pipe(
        switchMap(() => this.therapiesAC.getViewConfig()),
        tap(viewConfig => viewConfig.forms?.forEach(formId => this.formHandlerService.getForm(formId))),
        take(1)
      )
      .subscribe();
  }

  public showTab(tabName: string) {
    let visible;
    if (!this.currentRevision || !this.configuration || this.patientAnalyses == undefined || this.patientAnalyses == null) {
      return;
    }

    if (this.currentRevision.activeTabs) {
      return !!this.currentRevision.activeTabs.find(item => item.type.toLowerCase() === tabName.toLowerCase());
    }

    let showTestTabs = this.showTestSummaryAndReportPreviewTabs();

    switch (tabName) {
      case 'aberrations':
        visible = this.isUIRelevantJob() || ['test completed', 'finalreview', 'clinicaltrialmatch', 'therapymatch', 'uimanualaberrationcuration'].indexOf(this.currentRevision.activeJob.type.toLowerCase()) !== -1
          || this.showAberrationsTabWhileAnnotating();
        break;
      case 'prioritize':
        visible = (['interpretationresults', 'finalreview', 'test completed', 'uimanualaberrationcuration', 'therapymatchreflex', 'clinicalrialmatchreflex'].indexOf(this.currentRevision.activeJob.type.toLowerCase()) !== -1
          || this.currentRevision.status.toLowerCase() === 'test completed') && this.prioritizeAvailable > -1;
        break;
      case 'therapy':
        visible = (['interpretationresults', 'test completed'].indexOf(this.currentRevision.activeJob.type.toLowerCase()) !== -1
          || this.currentRevision.status.toLowerCase() === 'test completed') && this.prioritizeAvailable < 0 && !this.forceHideTab(tabName);
        break;
      case 'trials':
        visible = (['interpretationresults', 'test completed'].indexOf(this.currentRevision.activeJob.type.toLowerCase()) !== -1
          || this.currentRevision.status.toLowerCase() === 'test completed') && this.prioritizeAvailable < 0 && !this.forceHideTab(tabName);
        break;
      case 'case-summary':
        visible = this.configuration.caseSummaryTab &&
          (['test completed', 'finalreview'].indexOf(this.currentRevision.activeJob.type.toLowerCase()) !== -1
            || this.currentRevision.status.toLowerCase() === 'test completed');
        break;
      case 'test-summary':
        visible = !showTestTabs &&
          ((this.currentRevision.activeJob.type.toLowerCase() === 'interpretationresults' && this.prioritizeAvailable !== 2)
            || ['test completed', 'finalreview'].indexOf(this.currentRevision.activeJob.type.toLowerCase()) !== -1
            || this.currentRevision.status.toLowerCase() === 'test completed');
        break;
      case 'reportPreview':
        visible = showTestTabs && (((this.currentRevision.activeJob.type.toLowerCase() === 'interpretationresults' || this.currentRevision.activeJob.type.toLowerCase() === 'therapymatchreflex') && this.prioritizeAvailable !== 2)
          || ['test completed', 'finalreview'].indexOf(this.currentRevision.activeJob.type.toLowerCase()) !== -1
          || this.currentRevision.status.toLowerCase() === 'test completed'
          || (this.currentRevision.status.toLowerCase() === 'stopped' && _.get(this.configuration, 'stopAction.renderReport', false)));
        break;
      case 'qc-results':
        visible = this.qc;
        break;
      case 'specimens-and-samples':
        visible = this.testConfigurationInitialized() && this.configuration.specimensTabConfig && this.configuration.specimensTabConfig.specimensTabEnabled;
        break;
      case 'info':
        visible = this.testConfigurationInitialized();
        break;
    }
    if (this.configuration.tabVisibility && tabName != 'qc-results' && tabName != 'specimens-and-samples'
      && tabName != 'info') {
      visible = visible && this.configuration.tabVisibility[tabName];
    }
    return visible;
  }

  ngOnDestroy() {
    this.testService.updateButtonState(buttonsNamesEnum.NONE);
  }
}
